import {
    Button as FoodsButton,
    Typography as FoodsTypography,
    FoodsIcon,
    FoodsIllustrations,
    WhiteBox,
} from "@foodpilot/foods";
import { AccordionDetails, Box, Button, Divider, Stack, Typography, Pagination, PaginationItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { t } from "i18next";
import { Link, useNavigate, useParams } from "react-router";
import { useGetOrganizationPost } from "src/api/client-api/post/useGetOrganizationPost";
import {
    Property,
    SsqSsqHeading,
    SsqSsqHeadingSsqProperty,
    useGetOneSsqVersion,
} from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { useGetSomeSsq } from "src/api/sure-api/ssq/useGetSomeSsq";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getVisibleHeadings, isPropertyHidden } from "src/utils/ssq";
import { URLHandler } from "src/services/URLHandler";
import { ChangeEvent, useState, useMemo } from "react";
import { Loading } from "src/components/utils/Loading";
import { Post } from "src/api/client-api/post/postSchema";
import { CompanyService } from "../CompanyService";
import {
    Accordion,
    AccordionSummary,
    ImageContainer,
    imageContainerClasses,
} from "./components/LegalRequirementsStyles";
import { ScoreService } from "src/services/ScoreService";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";

const useRegulationSsqId = (selectedCompany?: any, platformScores?: any[], loadedSsqs?: any[]) => {
    return useMemo(() => {
        if (!selectedCompany || !platformScores || !loadedSsqs) {
            return undefined;
        }
        return ScoreService.getSsqFromCompanyAndDisplayMode(selectedCompany, platformScores, loadedSsqs, DISPLAY_MODE)
            ?.id;
    }, [selectedCompany, platformScores, loadedSsqs]);
};

const ITEMS_COUNT = 5;
const DISPLAY_MODE = "regulation";

const QuestionnaireNotice = () => {
    const { company, ssqVersion } = useCompanyMetadata();
    const { scoreId } = useParams();
    const { currentCampaign } = useFoodpilotContext();

    const ssqUrl = URLHandler.company.questionnaireScore(
        company.id,
        ssqVersion?.ssq.id ?? -1,
        currentCampaign?.id ?? -1,
        scoreId ?? "",
    );

    return (
        <Stack spacing={3} mb={0}>
            <Box
                sx={{
                    padding: (theme) => ({ xs: theme.spacing(2), md: theme.spacing(2.5, 4) }),
                    borderRadius: (theme) => theme.spacing(1),
                    border: (theme) => `1px solid ${theme.custom.grey[500]}`,
                    background: (theme) => theme.custom.grey[200],
                }}
            >
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "center" }}>
                    <Stack
                        direction={{ xs: "column", md: "row" }}
                        gap={2}
                        alignItems={{ xs: "flex-start", md: "center" }}
                        flex={1}
                        minWidth={0}
                    >
                        <Box flexShrink={0}>
                            <FoodsIcon icon="warning" size={6} />
                        </Box>
                        <Box minWidth={0} sx={{ overflowWrap: "anywhere" }}>
                            <FoodsTypography variant="body-semibold">
                                {t("bred.requirement.questionnaire_partial")}
                            </FoodsTypography>
                        </Box>
                    </Stack>
                    {currentCampaign?.id && (
                        <Link to={ssqUrl}>
                            <FoodsButton sx={{ width: { xs: "100%", md: "auto" }, whiteSpace: "normal" }}>
                                {t("questionnaire.complete_questionnaire")}
                            </FoodsButton>
                        </Link>
                    )}
                </Stack>
            </Box>
        </Stack>
    );
};

type Required = {
    required: true;
    title: string;
    description: string;
    tags: string[];
    imageUrl?: string;
    url?: string;
};
type NotRequired = { required: false };
type RuleContent = Required | NotRequired;

export const isRequired = (rc: RuleContent): rc is Required => {
    return (rc as Required).required;
};

export const extractRulesScore = (post: Post | undefined, rulesScoreId: number | undefined) => {
    if (rulesScoreId === undefined) return [];

    const rules = OlapScoresService.normalizeJsonScore(post?.olapScores[rulesScoreId]?.score.value);

    return post && rulesScoreId && rules !== null ? (Object.values(rules) as RuleContent[]).filter(isRequired) : [];
};

export const useIsRulesPostCompleted = (
    companyId?: number,
    campaignId?: number,
    rulesScoreId?: number,
    ssqVersionId?: number,
) => {
    const { ssqVersion } = useGetOneSsqVersion(ssqVersionId);
    const ssqId = ssqVersion?.ssq.id;

    const { data: post } = useGetOrganizationPost({ companyId, ssqId, campaignId });

    if (!companyId || !campaignId || !rulesScoreId || !post?.elements || !ssqVersion) return null;

    // Find all properties in headings that have formulas with the given rulesScoreId
    const findPropertiesWithRuleId = (ssqSsqHeading: SsqSsqHeading): number[] => {
        const result: number[] = [];

        const checkProperty = (prop: Property) => {
            const hasRuleFormula = prop.formulas?.some((formula) => formula.score && formula.score.id === rulesScoreId);
            const isConditionallyHidden = isPropertyHidden(
                prop.id,
                post.hiddenElements,
                ssqSsqHeading.ssqVersionStructureId,
            );

            if (hasRuleFormula && !isConditionallyHidden) {
                result.push(prop.id);
            }
        };

        ssqSsqHeading.ssqSsqHeadingSsqProperties
            ?.map((ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => ssqSsqHeadingSsqProperty.ssqProperty)
            ?.forEach(checkProperty);
        ssqSsqHeading.ssqSsqHeadingSsqProperties
            ?.map((ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => ssqSsqHeadingSsqProperty.ssqProperty)
            .forEach((prop: Property) => {
                prop.children?.forEach(checkProperty);
            });
        ssqSsqHeading.children?.forEach((child: SsqSsqHeading) => result.push(...findPropertiesWithRuleId(child)));
        return result;
    };

    const rulePropertyIds = ssqVersion.parentSsqSsqHeadings.flatMap(findPropertiesWithRuleId);

    // Check if we have answers for all these properties
    const answeredPropertyIds = post.elements
        .filter((element) => element.value !== "")
        .map((element) => element.ssqPropertyId);

    const unansweredProperties = rulePropertyIds.filter((id: number) => !answeredPropertyIds.includes(id));

    // Rule is completed if all required properties have answers
    return unansweredProperties.length === 0;
};

export const LegalRequirements = () => {
    const { currentCampaign, selectedCompany, getScoresByType } = useFoodpilotContext();
    const { ssqVersion } = useCompanyMetadata();

    // Get platform scores and SSQ data
    const platformScores = getScoresByType("company");
    const ssqId = ssqVersion?.ssq.id;

    const rulesScoreId = ScoreService.getScoreFromDisplayMode(platformScores, DISPLAY_MODE)?.id;

    const { post, isLoading } = useGetOrganizationPost({
        companyId: selectedCompany?.id,
        ssqId,
        campaignId: currentCampaign?.id,
    });

    const contents = extractRulesScore(post, rulesScoreId);
    const ssqSsqHeadings = ssqVersion ? getVisibleHeadings(ssqVersion, post?.hiddenElements) : [];
    const total = post?.completions.ssq.result ?? 0;

    const [page, setPage] = useState(0);
    const handlePageChange = (event: ChangeEvent<unknown>, value: number) => {
        setPage(value - 1);
    };

    const isRulesPostCompleted = useIsRulesPostCompleted(
        selectedCompany?.id,
        currentCampaign?.id,
        rulesScoreId,
        ssqVersion?.id,
    );

    const getLegalUrl = () => {
        if (selectedCompany === null || !ssqId || !currentCampaign?.id) return "";
        if (ssqSsqHeadings.length === 0) return "";

        const headingId = ssqSsqHeadings[0].id;
        const url = URLHandler.company.questionnaireHeading(
            selectedCompany.id,
            ssqId,
            currentCampaign.id,
            rulesScoreId,
            headingId,
        );
        return url;
    };

    if (isLoading) {
        return <Loading />;
    }

    // if no regulation SSQ found, display an error message
    if (!ssqId) {
        return <NoRegulationFound />;
    }

    // If the regulation questionnaire is not completed
    if (total === 0 && ssqSsqHeadings[0]) {
        return (
            <Stack>
                <QuestionnaireNeeded
                    companyId={selectedCompany?.id}
                    headingId={ssqSsqHeadings[0].id}
                    ssqId={ssqId}
                    scoreId={rulesScoreId}
                />
            </Stack>
        );
    }

    return (
        <Stack gap={3.5}>
            {isLoading && <Loading />}
            <NoneRequired contents={contents} isLoading={isLoading} isRulesPostCompleted={isRulesPostCompleted} />
            {!isLoading && contents.length > 0 && (
                <>
                    <InfoBox />
                    <Stack sx={{ py: 3, backgroundColor: "white" }} borderRadius={2} divider={<Divider />} gap={2}>
                        <Stack
                            direction={{ xs: "column", md: "row" }}
                            justifyContent="space-between"
                            alignItems={{ xs: "stretch", md: "center" }}
                            gap={2}
                            px={{ xs: 2, md: 4 }}
                        >
                            <Stack direction="row" alignItems="baseline" flexWrap="wrap" gap={1} minWidth={0}>
                                <Box minWidth={0} sx={{ overflowWrap: "anywhere" }}>
                                    <FoodsTypography variant="h3">{t("bred.requirement")}</FoodsTypography>
                                </Box>
                                <FoodsTypography variant="huge">- {contents.length}</FoodsTypography>
                            </Stack>
                            <Link to={getLegalUrl()}>
                                <FoodsButton variant="primary" sx={{ width: { xs: "100%", md: "auto" } }}>
                                    {t("questionnaire.modify_answers")}
                                </FoodsButton>
                            </Link>
                        </Stack>
                        <Box px={{ xs: 2, md: 4 }}>
                            {isRulesPostCompleted === false && <QuestionnaireNotice />}
                            {contents.slice(page * ITEMS_COUNT, (page + 1) * ITEMS_COUNT).map((p, i) => (
                                <Content key={i} content={p} defaultExpanded />
                            ))}

                            <Stack direction={"row"} justifyContent={"end"}>
                                {contents.length > ITEMS_COUNT && (
                                    <Pagination
                                        count={Math.ceil(contents.length / ITEMS_COUNT)}
                                        disabled={contents.length < ITEMS_COUNT}
                                        page={page + 1}
                                        onChange={handlePageChange}
                                        color="primary"
                                        renderItem={(item) => (
                                            <PaginationItem
                                                slots={{
                                                    previous: () => <FoodsIcon icon="arrowLeft" size={1} />,
                                                    next: () => <FoodsIcon icon="arrowRight" size={1} />,
                                                }}
                                                {...item}
                                            />
                                        )}
                                    />
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </>
            )}
        </Stack>
    );
};

const InfoBox = () => {
    const { currentCampaign, selectedCompany, getScoresByType } = useFoodpilotContext();

    // Get regulation SSQ data
    const platformScores = getScoresByType("company");
    const ssqIds = ScoreService.extractSsqIdsFromCompany(selectedCompany?.organizationSector);
    const { ssqs: loadedSsqs } = useGetSomeSsq(ssqIds.length > 0 ? ssqIds : undefined);
    const ssqId = useRegulationSsqId(selectedCompany, platformScores, loadedSsqs);

    const { isLoading } = useGetOrganizationPost({
        companyId: selectedCompany?.id,
        ssqId,
        campaignId: currentCampaign?.id,
    });
    const [isVisible, setIsVisible] = useState(() => {
        const status = CompanyService.getRegulatoryInfoBoxStatus();
        return status === null || status === "true";
    });

    if (!isVisible || isLoading) return null;

    const handleClick = () => {
        CompanyService.updateRegulatoryInfoBox(false);
        setIsVisible(false);
    };

    return (
        <Stack
            sx={{
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: (theme) => theme.spacing(1),
            }}
            px={{ xs: 2, md: 3 }}
            py={2.5}
        >
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
                <Stack direction="row" alignItems="center" gap={{ xs: 1, md: 2.5 }} minWidth={0}>
                    <Stack flexShrink={0}>
                        <FoodsIcon icon="information" size={3} />
                    </Stack>
                    <Box minWidth={0} sx={{ overflowWrap: "anywhere" }}>
                        <FoodsTypography variant="h5">{t("bred.requirement.info")}</FoodsTypography>
                    </Box>
                </Stack>
                <FoodsButton onClick={handleClick} sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}>
                    {t("bred.requirement.info.understood", "J'ai compris")}
                </FoodsButton>
            </Stack>
        </Stack>
    );
};

type QuestionnaireNeededProps = {
    companyId?: number;
    headingId: number;
    ssqId?: number;
    scoreId?: string;
};

export const QuestionnaireNeeded = (props: QuestionnaireNeededProps) => {
    const { companyId, headingId, ssqId: propSsqId, scoreId } = props;
    const { selectedCompany, currentCampaign, getScoresByType } = useFoodpilotContext();

    // Get regulation SSQ data as fallback
    const platformScores = getScoresByType("company");
    const ssqIds = ScoreService.extractSsqIdsFromCompany(selectedCompany?.organizationSector);
    const { ssqs: loadedSsqs } = useGetSomeSsq(ssqIds.length > 0 ? ssqIds : undefined);
    const fallbackSsqId = useRegulationSsqId(selectedCompany, platformScores, loadedSsqs);

    const ssqId = propSsqId || fallbackSsqId;
    const navigate = useNavigate();
    const handleClick = () => {
        if (!companyId || !selectedCompany || !currentCampaign?.id || !ssqId) return;
        const url = URLHandler.company.questionnaireHeading(
            companyId,
            ssqId,
            currentCampaign.id,
            scoreId ?? "",
            headingId,
        );
        navigate(url);
    };

    return (
        <WhiteBox sx={{ p: 3 }}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent={"space-between"}
                divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />}
                gap={{ xs: 3, md: 0 }}
            >
                <Stack flex={3} direction={"row"} gap={2} justifySelf={"start"}>
                    <FoodsIllustrations illustration="questionnaire" />
                    <Stack justifyContent={"center"} gap={1}>
                        <Typography variant="body-bold">
                            {t("bred.requirements.info.complete_questionnaire")}
                        </Typography>
                        <Typography variant="body">{t("bred.requirements.info.questionnaire_completed")}</Typography>
                    </Stack>
                </Stack>
                <Stack flex={{ xs: "auto", md: 1 }} justifyContent={"center"} alignItems={"center"}>
                    <Button variant="primary" onClick={handleClick} sx={{ width: { xs: "100%", md: "auto" } }}>
                        {t("questionnaire.complete_questionnaire")}
                    </Button>
                </Stack>
            </Stack>
        </WhiteBox>
    );
};

const NoRegulationFound = () => {
    return (
        <WhiteBox sx={{ p: 3 }}>
            <Stack direction={"column"} alignItems={"center"} justifyContent={"center"} gap={3}>
                <Stack
                    borderRadius={9999}
                    sx={{ backgroundColor: (theme) => theme.custom.orange[100] }}
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"56px"}
                    height={"56px"}
                >
                    <FoodsIcon icon="warning" size={4} />
                </Stack>
                <Stack direction={"column"} alignItems={"center"} gap={1}>
                    <Typography variant="h4">
                        {t("bred.requirement.no_regulation_ssq_found", "Aucun questionnaire réglementaire trouvé")}
                    </Typography>
                    <Typography variant="body" sx={{ textAlign: "center", maxWidth: "500px" }}>
                        {t(
                            "bred.requirement.no_regulation_ssq_description",
                            "Aucun questionnaire (SSQ) contenant des scores de type 'regulation' n'a été trouvé pour cette entreprise.",
                        )}
                    </Typography>
                </Stack>
            </Stack>
        </WhiteBox>
    );
};

type NoneRequiredProps = {
    contents: Required[];
    isLoading: boolean;
    isRulesPostCompleted: boolean | null;
};

const NoneRequired = (props: NoneRequiredProps) => {
    const { contents, isLoading, isRulesPostCompleted } = props;

    if (isLoading || contents.length > 0) return null;

    return (
        <Stack sx={{ backgroundColor: "white" }} borderRadius={2} p={4} gap={4}>
            {isRulesPostCompleted === false && <QuestionnaireNotice />}
            <Stack direction={"column"} alignItems={"center"} justifyContent={"center"} gap={3}>
                <Stack
                    borderRadius={9999}
                    sx={{ backgroundColor: (theme) => theme.custom.green[100] }}
                    justifyContent={"center"}
                    alignItems={"center"}
                    width={"56px"}
                    height={"56px"}
                >
                    <FoodsIcon icon="check" size={4} />
                </Stack>
                <Typography variant="h4">{t("bred.requirement.none_required")}</Typography>
            </Stack>
        </Stack>
    );
};

type ContentProps = {
    content: Required;
    defaultExpanded: boolean;
};
const Content = (props: ContentProps) => {
    const { content, defaultExpanded } = props;

    return (
        <Accordion elevation={0} defaultExpanded={defaultExpanded}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "stretch", md: "baseline" }}
                    gap={2}
                    minWidth={0}
                    width="100%"
                    sx={{ overflowWrap: "anywhere" }}
                >
                    <Box minWidth={0} flex={1}>
                        <FoodsTypography variant="h4">{content.title}</FoodsTypography>
                    </Box>
                    <Stack
                        direction="row"
                        flexWrap="wrap"
                        gap={1}
                        minWidth={0}
                        sx={{ maxWidth: "100%", flexShrink: 1 }}
                    >
                        {content.tags.map((tag, index) => (
                            <Box
                                className="category"
                                key={index}
                                sx={{ backgroundColor: (theme) => theme.custom.grey[400], maxWidth: "100%" }}
                                px={1}
                                py={0.5}
                                borderRadius={1}
                            >
                                <FoodsTypography variant="small-bold">{tag}</FoodsTypography>
                            </Box>
                        ))}
                    </Stack>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Stack pt={1} direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
                    {content.imageUrl && (
                        <ImageContainer>
                            <img src={content.imageUrl} alt={content.title} className={imageContainerClasses.image} />
                        </ImageContainer>
                    )}
                    <Box flex={1}>
                        <Box pb={2} fontSize={12} fontWeight={500} lineHeight={2}>
                            {content.description}
                        </Box>
                        {content.url && (
                            <Button variant="ternary" sx={{ width: "fit-content" }} href={content.url} target="_blank">
                                <Stack direction={"row"} gap={1} alignItems={"center"}>
                                    <Typography fontSize={12} fontWeight={700}>
                                        {t("bred.requirement.info.more_info")}
                                    </Typography>
                                    <FoodsIcon icon="openNewTab" size={1} />
                                </Stack>
                            </Button>
                        )}
                    </Box>
                </Stack>
            </AccordionDetails>
        </Accordion>
    );
};
